<script setup lang="ts">
  import { ref, watch, computed } from 'vue';
  import { Loader2 } from 'lucide-vue-next';
  import { useModal } from 'vue-final-modal';
  import { IconImage, Checkbox, Tooltip, InfoTooltip, ConfirmDeleteModal } from '@/shared/ui';
  import { useCustomToast } from '@/shared/lib';
  import PencilIcon from '@/shared/assets/icons/PencilGray.svg';
  import Trash from '@/shared/assets/icons/TrashIcon.svg';
  import InfoIcon from '@/shared/assets/icons/InfoIcon.svg';
  import SmallChevronUpIcon from '@/shared/assets/icons/SmallChevronUp.svg';
  import { departmentApi, type Department } from '@/entities/Department';
  import { positionApi, type Position } from '@/entities/Position';
  import { formatMemberAuditDateTime } from '@/pages/admin/lib';
  import PositionChildRow from './PositionChildRow.vue';

  const props = defineProps<{
    department: Department;
    isExpanded: boolean;
    isSelected: boolean;
  }>();

  const emit = defineEmits<{
    'toggle-selection': [id: number];
    'toggle-expand': [];
    edit: [id: number];
    delete: [id: number];
    info: [id: number];
  }>();

  const toggleSelection = () => {
    emit('toggle-selection', props.department.id);
  };

  const positions = ref<Position[]>([]);
  const loadingPositions = ref(false);
  const positionsLoaded = ref(false);
  const DEPARTMENT_POSITIONS_LIMIT = 100;
  const selectedPositionId = ref<number | null>(null);
  const toast = useCustomToast();

  const canFetchPositions = computed(() => !positionsLoaded.value && !loadingPositions.value);

  const toggleExpand = () => {
    emit('toggle-expand');
  };

  watch(
    () => props.isExpanded,
    async (expanded) => {
      if (expanded && canFetchPositions.value) {
        loadingPositions.value = true;
        try {
          const result = await positionApi.searchPositions({
            departmentId: props.department.id,
            limit: DEPARTMENT_POSITIONS_LIMIT,
          });
          positions.value = result.data;
          positionsLoaded.value = true;
        } catch (error) {
          console.error('Erro ao carregar cargos:', error);
        } finally {
          loadingPositions.value = false;
        }
      }
    },
    { immediate: true }
  );

  const handleEdit = () => {
    emit('edit', props.department.id);
  };

  const handleDelete = () => {
    emit('delete', props.department.id);
  };

  const handleInfo = () => {
    emit('info', props.department.id);
  };

  const handlePositionInfo = () => {
    // TODO: Implementar tooltip de informações do cargo
  };

  const handlePositionDelete = (positionId: number) => {
    selectedPositionId.value = positionId;
    openDeleteConfirmationModal();
  };

  const { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal } = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      title: 'Tem certeza que deseja deletar o Cargo?',
      description: 'A ação não pode ser desfeita.',
      onClose: () => {
        closeDeleteConfirmationModal();
        selectedPositionId.value = null;
      },
      onConfirm: async () => {
        closeDeleteConfirmationModal();
        if (selectedPositionId.value === null) return;

        try {
          await departmentApi.updatePositions(props.department.id, {
            removePositionIds: [selectedPositionId.value],
          });

          toast.showSuccessToast({
            title: 'Cargo deletado com sucesso',
            type: 'success',
          });

          positions.value = positions.value.filter((p) => p.id !== selectedPositionId.value);
          selectedPositionId.value = null;
        } catch (error) {
          console.error('Erro ao deletar cargo:', error);
          toast.showErrorToast({
            title: 'Erro ao deletar cargo',
            message: 'Não foi possível deletar o cargo. Tente novamente.',
            type: 'error',
          });
        }
      },
    },
  });

  const actions = [
    { icon: PencilIcon, tooltip: 'Editar', handler: handleEdit },
    { icon: Trash, tooltip: 'Deletar', handler: handleDelete },
  ];
</script>

<template>
  <tr class="transition-colors duration-300 ease-out">
    <td class="w-20 align-middle text-center">
      <Checkbox
        :id="department.id"
        :checked="isSelected"
        @toggle="toggleSelection"
      />
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ department.code }}
    </td>
    <td class="pl-6 py-4 text-sm text-gray-07 w-50">
      <div class="flex gap-2 text-gray-xx-02 items-center">
        <Tooltip
          v-for="action in actions"
          :key="action.tooltip"
          :content="action.tooltip"
          placement="top"
          class="flex items-center"
        >
          <button @click="action.handler">
            <IconImage
              :src="action.icon"
              :alt="action.tooltip"
            />
          </button>
        </Tooltip>
        <InfoTooltip
          :icon="InfoIcon"
          title="Informações"
          :created-at="formatMemberAuditDateTime(department.createdAt)"
          :created-by="department.creator?.name"
          :updated-at="formatMemberAuditDateTime(department.updatedAt)"
          :updated-by="department.updater?.name"
          placement="top"
        >
          <button @click="handleInfo">
            <IconImage
              :src="InfoIcon"
              alt="Informações"
            />
          </button>
        </InfoTooltip>
      </div>
    </td>
    <td class="w-12 text-center">
      <button
        class="p-2 border border-gray-02 rounded-lg shadow-sm"
        @click="toggleExpand"
      >
        <IconImage
          :src="SmallChevronUpIcon"
          alt="Expandir"
          class="transition-transform duration-200"
          :class="{ 'rotate-180': !props.isExpanded }"
        />
      </button>
    </td>
  </tr>
  <template v-if="props.isExpanded">
    <tr v-if="loadingPositions">
      <td
        colspan="4"
        class="bg-gray-02/30 pl-12 py-3 text-sm text-gray-05"
      >
        <div class="flex items-center gap-2">
          <Loader2 class="w-4 h-4 animate-spin text-gray-05" />
          Carregando cargos...
        </div>
      </td>
    </tr>
    <template v-else>
      <tr v-if="positions.length === 0">
        <td
          colspan="4"
          class="bg-gray-02/30 pl-12 py-3 text-sm text-gray-05"
        >
          Nenhum cargo encontrado neste departamento.
        </td>
      </tr>
      <template v-else>
        <PositionChildRow
          v-for="position in positions"
          :key="position.id"
          :position="position"
          @info="handlePositionInfo"
          @delete="handlePositionDelete"
        />
      </template>
    </template>
  </template>
</template>
